import { getAppMaster, getDynamoDB } from '../../../../common/utils/DynamoDbUtils'
import { table, restApiKey } from '../../../../_constants/Code'
import { PathInfoList } from '../../../../_constants/PathInfoList'
import { httpGet, httpPost } from '../../../../services/api/AmplifyApi'
import { DataClassificationSchema, DataColumnSchema, JudgmentRuleSchema, JudgmentRuleRequestSchema } from './types'

/**
 * セッション情報を取得
 *
 * @param {any} store - Redux store（グローバルストアまたはカスタムストア）
 * @param {Object} props - コンポーネントのprops（corporationNumber, userIdを含む）
 * @returns {{ corporationNumber: string; userId: string }}
 */
const getSession = (store, props = null) => {
  // propsから直接取得できる場合は優先
  if (props && props.corporationNumber && props.userId) {
    return {
      corporationNumber: props.corporationNumber,
      userId: props.userId
    }
  }

  // ストアから取得を試みる
  const state = store.getState()
  // カスタムストアの場合はmainStoreから取得、グローバルストアの場合は直接取得
  const mainState = state.mainStore || state
  return {
    corporationNumber: mainState?.company?.corporationNumber,
    userId: mainState?.user?.sub
  }
}

const DATA_CLASSIFICATION_SUFFIX = '#DataClassification'
const CLASSIFICATION_ITEMS_PREFIX = '#classification#items#'
const DEFAULT_GENERIC_DATA_KIND_DISPLAY_ORDER = 9999
const DEFAULT_CHROFY_COLUMN_DISPLAY_ORDER = 9999

/**
 * GenericDataKindDisplayOrder 昇順で比較する（同順は GenericDataKindCode 昇順）
 *
 * @param {{ GenericDataKindDisplayOrder?: number; DataClassificationCode?: string }} a
 * @param {{ GenericDataKindDisplayOrder?: number; DataClassificationCode?: string }} b
 * @returns {number}
 */
const compareByGenericDataKindDisplayOrder = (a, b) => {
  const orderA = a.GenericDataKindDisplayOrder ?? DEFAULT_GENERIC_DATA_KIND_DISPLAY_ORDER
  const orderB = b.GenericDataKindDisplayOrder ?? DEFAULT_GENERIC_DATA_KIND_DISPLAY_ORDER
  if (orderA !== orderB) return orderA - orderB
  return (a.DataClassificationCode || '').localeCompare(b.DataClassificationCode || '')
}

/**
 * DisplayOrder 昇順で比較する（同順は ChrofyColumnCode 昇順）
 *
 * @param {{ DisplayOrder?: number; ColumnCode?: string }} a
 * @param {{ DisplayOrder?: number; ColumnCode?: string }} b
 * @returns {number}
 */
const compareByChrofyColumnDisplayOrder = (a, b) => {
  const orderA = a.DisplayOrder ?? DEFAULT_CHROFY_COLUMN_DISPLAY_ORDER
  const orderB = b.DisplayOrder ?? DEFAULT_CHROFY_COLUMN_DISPLAY_ORDER
  if (orderA !== orderB) return orderA - orderB
  return (a.ColumnCode || '').localeCompare(b.ColumnCode || '')
}

/**
 * データ分類コードに紐づくデータ項目一覧を返す（buildChrofyColumns でソート済みの順序を維持）
 *
 * @param {import('./types').DataColumn[]} chrofyColumns
 * @param {string} categoryCode
 * @returns {import('./types').DataColumn[]}
 */
export const getChrofyColumnsByDataClassificationCode = (chrofyColumns, categoryCode) => {
  if (!categoryCode) return []
  return (chrofyColumns || []).filter((col) => col.DataClassificationCode === categoryCode)
}

/**
 * APIレスポンスを取得
 *
 * @param {any} response - HttpGet の戻り値
 * @returns {Object}
 */
const getSubDataResponseBody = (response) => {
  if (response != null && typeof response === 'object') return response
  return {}
}

/**
 * DataClassifications を表示用に整え（RANK関数モーダル用）
 *
 * @param {Array} rawClassifications - APIの dataClassifications 配列
 * @returns {import('./types').DataClassification[]}
 */
const buildDataClassifications = (rawClassifications) => {
  const sorted = [...(rawClassifications || [])].sort((a, b) => {
    const orderA = a.DataClassificationDisplayOrder ?? 9999
    const orderB = b.DataClassificationDisplayOrder ?? 9999
    if (orderA !== orderB) return orderA - orderB
    return (a.DataClassificationName ?? '').localeCompare(b.DataClassificationName ?? '')
  })
  return sorted
    .map((m) => {
      const searchKey = m.SearchKey || ''
      const code = searchKey.endsWith(DATA_CLASSIFICATION_SUFFIX)
        ? searchKey.slice(0, -DATA_CLASSIFICATION_SUFFIX.length)
        : searchKey
      const name = m.DataClassificationName ?? ''
      if (!code || !name) return null
      return { DataClassificationCode: code, DataClassificationName: name }
    })
    .filter(Boolean)
    .map((item) => DataClassificationSchema.parse(item))
}

/**
 * GenericDataKinds を TargetPeriod / PrimaryKeyColumnCodes で絞り込み、GenericDataKindDisplayOrder 昇順のデータ分類一覧を返す
 *
 * @param {Array} genericDataKinds - APIの genericDataKinds 配列
 * @param {string} periodToMatch - TargetPeriod に含まれるべき値（'year' | 'monthly'）
 * @param {string} primaryKeyToMatch - PrimaryKeyColumnCodes に含まれるべき値
 * @returns {import('./types').DataClassification[]}
 */
const buildGenericDataKindClassificationsByTargetPeriod = (genericDataKinds, periodToMatch, primaryKeyToMatch) => {
  if (!Array.isArray(genericDataKinds) || genericDataKinds.length === 0) return []

  const taskMap = new Map()
  genericDataKinds.forEach((task) => {
    const periods = task.TargetPeriod || []
    const primaryKey = task.PrimaryKeyColumnCodes || []
    if (
      Array.isArray(periods) &&
      periods.includes(periodToMatch) &&
      Array.isArray(primaryKey) &&
      primaryKey.includes(primaryKeyToMatch)
    ) {
      const code = task.GenericDataKindCode ?? ''
      const name = task.GenericDataKindName ?? ''
      if (code && name && !taskMap.has(code)) {
        taskMap.set(code, {
          DataClassificationCode: code,
          DataClassificationName: name,
          GenericDataKindDisplayOrder: task.GenericDataKindDisplayOrder ?? DEFAULT_GENERIC_DATA_KIND_DISPLAY_ORDER
        })
      }
    }
  })

  return Array.from(taskMap.values())
    .sort(compareByGenericDataKindDisplayOrder)
    .map(({ DataClassificationCode, DataClassificationName }) =>
      DataClassificationSchema.parse({ DataClassificationCode, DataClassificationName })
    )
}

/**
 * GenericDataKinds から判定結果の設定先用データ分類一覧を生成（重複除去）
 *
 * @param {Array} genericDataKinds - APIの genericDataKinds 配列
 * @returns {import('./types').DataClassification[]}
 */
const buildMappingTaskClassifications = (genericDataKinds) => {
  const taskMap = new Map()
  ;(genericDataKinds || []).forEach((t) => {
    const code = t.GenericDataKindCode ?? ''
    const name = t.GenericDataKindName ?? ''
    if (code && name && !taskMap.has(code)) {
      taskMap.set(code, {
        DataClassificationCode: code,
        DataClassificationName: name,
        GenericDataKindDisplayOrder: t.GenericDataKindDisplayOrder ?? DEFAULT_GENERIC_DATA_KIND_DISPLAY_ORDER
      })
    }
  })
  return Array.from(taskMap.values())
    .sort(compareByGenericDataKindDisplayOrder)
    .map(({ DataClassificationCode, DataClassificationName }) =>
      DataClassificationSchema.parse({ DataClassificationCode, DataClassificationName })
    )
}

/**
 * GenericDataKinds を対象期間で絞り込み、GenericDataKindDisplayOrder 昇順のデータ分類一覧を返す
 *
 * @param {Array} genericDataKinds - APIの genericDataKinds 配列
 * @param {'' | 'monthly' | 'quarter' | 'yearly'} periodKind - 対象期間
 * @returns {import('./types').DataClassification[]}
 */
export const buildFilteredGenericDataKindClassifications = (genericDataKinds, periodKind) => {
  if (!periodKind || (periodKind !== 'monthly' && periodKind !== 'quarter' && periodKind !== 'yearly')) return []

  const periodToMatch = periodKind === 'yearly' ? 'year' : periodKind === 'quarter' ? 'quarter' : 'monthly'
  const primaryKeyToMatch =
    periodKind === 'yearly' ? 'G0008-0018' : periodKind === 'quarter' ? 'G0007-0018' : 'G0006-0018'
  return buildGenericDataKindClassificationsByTargetPeriod(genericDataKinds, periodToMatch, primaryKeyToMatch)
}

/**
 * サイドバー用: genericDataKinds をデータタイプに応じて絞り込み、GenericDataKindDisplayOrder 昇順のデータ分類一覧を返す
 *
 * @param {Array} genericDataKinds - APIの genericDataKinds 配列
 * @param {'monthly' | 'quarterly' | 'yearly' | 'yearEndMonthly'} effectiveDataType - サイドバーのデータタイプ
 * @returns {import('./types').DataClassification[]}
 */
export const buildSidebarGenericDataKindClassifications = (genericDataKinds, effectiveDataType) => {
  if (
    !effectiveDataType ||
    (effectiveDataType !== 'monthly' &&
      effectiveDataType !== 'quarterly' &&
      effectiveDataType !== 'yearly' &&
      effectiveDataType !== 'yearEndMonthly')
  ) {
    return []
  }

  const periodToMatch =
    effectiveDataType === 'yearly' ? 'year' : effectiveDataType === 'quarterly' ? 'quarter' : 'monthly'
  const primaryKeyToMatch =
    effectiveDataType === 'yearly' ? 'G0008-0018' : effectiveDataType === 'quarterly' ? 'G0007-0018' : 'G0006-0018'
  return buildGenericDataKindClassificationsByTargetPeriod(genericDataKinds, periodToMatch, primaryKeyToMatch)
}

/**
 * GenericChrofyColumns から chrofyColumns を生成（判定結果の設定先・サイドバー用）
 *
 * @param {Array} rawChrofyColumns - APIの genericChrofyColumns 配列
 * @returns {import('./types').DataColumn[]}
 */
const buildChrofyColumns = (rawChrofyColumns) => {
  return (rawChrofyColumns || [])
    .map((m) => {
      const dataClassificationCode = m.DataKindCode ?? m.DataClassificationCode ?? ''
      const columnCode = m.ChrofyColumnCode ?? m.ColumnCode ?? ''
      const columnName = m.ColumnName ?? ''
      if (!dataClassificationCode || !columnCode || !columnName) return null
      return {
        DataClassificationCode: dataClassificationCode,
        ColumnCode: columnCode,
        ColumnName: columnName,
        DisplayOrder: m.DisplayOrder ?? DEFAULT_CHROFY_COLUMN_DISPLAY_ORDER
      }
    })
    .filter(Boolean)
    .sort((a, b) => {
      const categoryCompare = (a.DataClassificationCode || '').localeCompare(b.DataClassificationCode || '')
      if (categoryCompare !== 0) return categoryCompare
      return compareByChrofyColumnDisplayOrder(a, b)
    })
    .map(({ DataClassificationCode, ColumnCode, ColumnName }) =>
      DataColumnSchema.parse({ DataClassificationCode, ColumnCode, ColumnName })
    )
}

/**
 * ClassificationItems から dataColumns を生成（後に使う実装用、現状 UI 未使用）
 *
 * @param {Array} rawItems - APIの classificationItems 配列
 * @returns {import('./types').DataColumn[]}
 */
const buildDataColumnsFromItems = (rawItems) => {
  const sorted = [...(rawItems || [])].sort((a, b) => {
    const orderA = a.ItemDisplayOrder ?? 9999
    const orderB = b.ItemDisplayOrder ?? 9999
    if (orderA !== orderB) return orderA - orderB
    return (a.ItemValue ?? '').localeCompare(b.ItemValue ?? '')
  })
  return sorted
    .map((m) => {
      const searchKey = m.SearchKey || ''
      const idx = searchKey.indexOf(CLASSIFICATION_ITEMS_PREFIX)
      const dataClassificationCode = idx >= 0 ? searchKey.slice(0, idx) : ''
      const columnCode = m.ItemCode ?? ''
      const columnName = m.ItemValue ?? ''
      if (!dataClassificationCode || !columnCode || !columnName) return null
      return {
        DataClassificationCode: dataClassificationCode,
        ColumnCode: columnCode,
        ColumnName: columnName
      }
    })
    .filter(Boolean)
    .map((item) => DataColumnSchema.parse(item))
}

/**
 * 判定ルールサブデータを get_judgment_rule_sub_data API で取得する
 *
 * - 判定結果の設定先 データ分類: mappingTaskClassifications (genericDataKinds から生成)
 * - 判定結果の設定先 データ項目: chrofyColumns (genericChrofyColumns から生成)
 * - サイドバー データ分類: genericDataKinds を TargetPeriod で絞り込み
 * - RANK関数 項目: dataClassifications
 * - ClassificationItems: 判定結果プルダウン用（ユーザー区分）
 * - ChrofyClassifications / chrofyClassificationItems: 判定結果プルダウン用（CHROFY規定値）
 *
 * @param {any} store - Redux store
 * @param {Object} props - コンポーネントの props（corporationNumber, userId を含む）
 */
export const fetchJudgmentRuleSubData = async (store, props = null) => {
  const { corporationNumber } = getSession(store, props)
  const response = await httpGet(
    restApiKey.CHROFY_REST_API,
    PathInfoList.Resource.getJudgmentRuleSubData(corporationNumber),
    { headers: {} }
  )

  const body = getSubDataResponseBody(response)
  const rawClassifications = Array.isArray(body.dataClassifications) ? body.dataClassifications : []
  const rawItems = Array.isArray(body.classificationItems) ? body.classificationItems : []
  const genericDataKinds = Array.isArray(body.genericDataKinds) ? body.genericDataKinds : []
  const rawChrofyColumns = Array.isArray(body.genericChrofyColumns) ? body.genericChrofyColumns : []
  const chrofyClassifications = Array.isArray(body.chrofyClassifications) ? body.chrofyClassifications : []
  const chrofyClassificationItems = Array.isArray(body.chrofyClassificationItems) ? body.chrofyClassificationItems : []

  const dataClassifications = buildDataClassifications(rawClassifications)
  const mappingTaskClassifications = buildMappingTaskClassifications(genericDataKinds)
  const chrofyColumns = buildChrofyColumns(rawChrofyColumns)
  const dataColumns = buildDataColumnsFromItems(rawItems)

  return {
    dataClassifications,
    rawDataClassifications: rawClassifications,
    classificationItems: rawItems,
    dataColumns,
    genericDataKinds,
    mappingTaskClassifications,
    chrofyColumns,
    chrofyClassifications,
    chrofyClassificationItems
  }
}

/**
 * データ分類一覧を取得する（モック利用時はコメント解除。通常は fetchJudgmentRuleSubData を使用）
 *
 * @param {any} store - Redux store
 * @returns {Promise<import('./types').DataClassification[]>}
 */
export const fetchDataClassifications = async (store, props = null) => {
  try {
    const { dataClassifications } = await fetchJudgmentRuleSubData(store, props)
    return dataClassifications
  } catch (error) {
    console.error('データ分類一覧取得エラー:', error)
    throw error
  }
}

/**
 * データ項目一覧を取得する（モック利用時はコメント解除。通常は fetchJudgmentRuleSubData を使用）
 *
 * @param {any} store - Redux store
 * @returns {Promise<import('./types').DataColumn[]>}
 */
export const fetchDataColumns = async (store, props = null) => {
  try {
    const { dataColumns } = await fetchJudgmentRuleSubData(store, props)
    return dataColumns
  } catch (error) {
    console.error('データ項目一覧取得エラー:', error)
    throw error
  }
}

/**
 * 既存の判定ルールを取得する（JudgmentRuleテーブルから）
 *
 * @param {any} store - Redux store
 * @param {string} ruleId - ルールID
 * @param {Object} props - コンポーネントのprops（corporationNumber, userIdを含む）
 * @returns {Promise<import('./types').JudgmentRule | null>}
 */
export const fetchRule = async (store, ruleId, props = null) => {
  try {
    const { corporationNumber } = getSession(store, props)

    // get_judgment_rule APIを使用して判定ルール一覧を取得
    const init = {
      headers: {}
    }
    const response = await httpGet(
      restApiKey.CHROFY_REST_API,
      PathInfoList.Resource.getJudgmentRule(corporationNumber),
      init
    )

    // レスポンスのbodyを取得
    const rows = response.body || (Array.isArray(response) ? response : [])

    if (!Array.isArray(rows)) {
      return null
    }

    // 指定されたruleIdに一致するルールを検索
    const rule = rows.find((r) => r.RuleId === ruleId)

    if (!rule || Object.keys(rule).length === 0) {
      return null
    }

    return JudgmentRuleSchema.parse(rule)
  } catch (error) {
    console.error('判定ルール取得エラー:', error)
    throw error
  }
}

/**
 * 判定ルールを登録または更新する
 *
 * @param {any} store - Redux store
 * @param {import('./types').JudgmentRuleRequest} request - 判定ルールリクエスト
 * @returns {Promise<{ RuleId: string }>}
 */
export const createOrUpdateRule = async (store, request, props = null) => {
  try {
    const { corporationNumber } = getSession(store, props)

    // リクエストをバリデーション
    const validatedRequest = JudgmentRuleRequestSchema.parse(request)

    const init = {
      body: {
        judgment_rule_data: {
          ...validatedRequest
        }
      },
      headers: {}
    }

    const result = await httpPost(
      restApiKey.CHROFY_REST_API,
      PathInfoList.Resource.createOrUpdateJudgmentRule(corporationNumber),
      init
    )

    return result
  } catch (error) {
    console.error('判定ルール登録・更新エラー:', error)
    throw error
  }
}
